// Provincial automobile-dealer association mapping for CADA, shared by the FAQ "About Provincial
// Association" section (ES-863) and the footer logo (ES-1008).

export type AssociationKey =
    | "ADANL"
    | "MDA"
    | "MMDA"
    | "MVRO"
    | "NBADA"
    | "NCDA"
    | "NSADA"
    | "PEIADA"
    | "SADA"
    | "CCAQ"
    | "CCAM";

// Province → provincial association mapping (ES-863).
// Montreal-area QC dealers see CCAM in addition to CCAQ; we use the postal-code
// "H" prefix as the Montreal heuristic since Canada Post reserves H for Montreal/Laval.
export function getAssociationsForProvince(
    province: string | undefined,
    zipCode: string | undefined
): AssociationKey[] {
    if (!province) return [];
    const provinceUpper = province.toUpperCase();
    switch (provinceUpper) {
        case "AB":
            return ["MDA"];
        case "BC":
            return ["NCDA"];
        case "MB":
            return ["MMDA"];
        case "NB":
            return ["NBADA"];
        case "NL":
            return ["ADANL"];
        case "NS":
            return ["NSADA"];
        case "ON":
            return ["MVRO"];
        case "PE":
            return ["PEIADA"];
        case "SK":
            return ["SADA"];
        case "QC": {
            const isMontreal = zipCode?.trim().toUpperCase().startsWith("H");
            return isMontreal ? ["CCAQ", "CCAM"] : ["CCAQ"];
        }
        default:
            return [];
    }
}

// Logo assets that ship in public/ for each association — one per provincial association. [ES-1008]
export const ASSOCIATION_LOGOS: Record<AssociationKey, string> = {
    NCDA: "/NCDA.png",
    MDA: "/MDA.png",
    MMDA: "/MMDA.png",
    MVRO: "/MVRO.png",
    SADA: "/SADA.png",
    NSADA: "/NSADA.png",
    CCAQ: "/CCAQ.png",
    NBADA: "/NBADA.png",
    ADANL: "/ADANL.png",
    PEIADA: "/PEI.png",
    CCAM: "/CCAM.png",
};

// The dealer's provincial association logo(s) for the footer, by province — NCDA is BC's
// association (not a national logo), so it only appears for BC dealers, just like MDA for AB,
// MVRO for ON, etc. [ES-1008]
export function getProvincialAssociationLogos(
    province: string | undefined,
    zipCode: string | undefined
): { key: AssociationKey; src: string }[] {
    return getAssociationsForProvince(province, zipCode).map((key) => ({
        key,
        src: ASSOCIATION_LOGOS[key],
    }));
}
